import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Loader2, Users, Trash2, AlertCircle, Crown, Shield, User, TrendingUp } from 'lucide-react'
import type { GroupMember } from './types'
import { getInitials, formatDate } from './utils'

/** Get ELO tier name for display */
function getEloTier(elo: number): string {
  if (elo >= 2200) return 'Master'
  if (elo >= 1900) return 'Expert'
  if (elo >= 1600) return 'Advanced'
  if (elo >= 1400) return 'Intermediate'
  if (elo >= 1200) return 'Casual'
  if (elo >= 1000) return 'Beginner'
  return 'Novice'
}

/** Get color class based on ELO tier */
function getEloColorClass(elo: number): string {
  if (elo >= 2200) return 'text-purple-600 dark:text-purple-400'
  if (elo >= 1900) return 'text-red-600 dark:text-red-400'
  if (elo >= 1600) return 'text-orange-600 dark:text-orange-400'
  if (elo >= 1400) return 'text-yellow-600 dark:text-yellow-400'
  if (elo >= 1200) return 'text-green-600 dark:text-green-400'
  if (elo >= 1000) return 'text-blue-600 dark:text-blue-400'
  return 'text-gray-600 dark:text-gray-400'
}

interface MembersTabProps {
  members: GroupMember[]
  membersLoading: boolean
  membersError: string | null
  // Actions
  canRemoveMember: (member: GroupMember) => boolean
  canChangeRole: (member: GroupMember) => boolean
  onRoleChange: (member: GroupMember, newRole: 'member' | 'admin') => void
  onOrganiserChange: (member: GroupMember, isOrganiser: boolean) => void
  onRemoveMember: (member: GroupMember) => void
  // Loading states
  removingMemberId: string | null
  updatingRoleId: string | null
  updatingOrganiserId: string | null
}

function RoleBadge({ role }: { role: string }) {
  switch (role) {
    case 'owner':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:text-yellow-400">
          <Crown className="h-3 w-3" />
          Owner
        </span>
      )
    case 'admin':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400">
          <Shield className="h-3 w-3" />
          Admin
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
          <User className="h-3 w-3" />
          Member
        </span>
      )
  }
}

export function MembersTab({
  members,
  membersLoading,
  membersError,
  canRemoveMember,
  canChangeRole,
  onRoleChange,
  onOrganiserChange,
  onRemoveMember,
  removingMemberId,
  updatingRoleId,
  updatingOrganiserId,
}: MembersTabProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <CardDescription>
          {members.length} {members.length === 1 ? 'member' : 'members'} in this group
        </CardDescription>
      </CardHeader>
      <CardContent>
        {membersLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : membersError ? (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {membersError}
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No members yet. Send invites to add members.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-center">Rating</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-center">Organiser</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.userId}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.avatar} alt={member.displayName} />
                            <AvatarFallback className="text-xs">
                              {getInitials(member.displayName)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{member.displayName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <RoleBadge role={member.role} />
                          {canChangeRole(member) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              disabled={updatingRoleId === member.userId}
                              onClick={() => onRoleChange(member, member.role === 'admin' ? 'member' : 'admin')}
                            >
                              {updatingRoleId === member.userId ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : member.role === 'admin' ? (
                                'Remove Admin'
                              ) : (
                                'Make Admin'
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="inline-flex items-center gap-1">
                                <TrendingUp className={`h-3.5 w-3.5 ${getEloColorClass(member.elo ?? 1200)}`} />
                                <span className={`font-medium ${getEloColorClass(member.elo ?? 1200)}`}>
                                  {member.elo ?? 1200}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{getEloTier(member.elo ?? 1200)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(member.joinedAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Switch
                            id={`org-${member.userId}`}
                            checked={member.isOrganiser || false}
                            disabled={updatingOrganiserId === member.userId}
                            onCheckedChange={(checked) => onOrganiserChange(member, checked)}
                          />
                          {updatingOrganiserId === member.userId && (
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {canRemoveMember(member) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={removingMemberId === member.userId}
                            onClick={() => onRemoveMember(member)}
                          >
                            {removingMemberId === member.userId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {members.map((member) => (
                <div key={member.userId} className="rounded-lg border p-4 space-y-3">
                  {/* Header with avatar and name */}
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.avatar} alt={member.displayName} />
                      <AvatarFallback className="text-sm">
                        {getInitials(member.displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{member.displayName}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Joined {formatDate(member.joinedAt)}</span>
                        <span className="text-muted-foreground/50">•</span>
                        <span className={`font-medium ${getEloColorClass(member.elo ?? 1200)}`}>
                          {member.elo ?? 1200} ELO
                        </span>
                      </div>
                    </div>
                    {canRemoveMember(member) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        disabled={removingMemberId === member.userId}
                        onClick={() => onRemoveMember(member)}
                      >
                        {removingMemberId === member.userId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Role and Actions */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2 flex-wrap">
                      <RoleBadge role={member.role} />
                      {canChangeRole(member) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={updatingRoleId === member.userId}
                          onClick={() => onRoleChange(member, member.role === 'admin' ? 'member' : 'admin')}
                        >
                          {updatingRoleId === member.userId ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : member.role === 'admin' ? (
                            'Remove Admin'
                          ) : (
                            'Make Admin'
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`org-mobile-${member.userId}`} className="text-xs text-muted-foreground">
                        Organiser
                      </Label>
                      <Switch
                        id={`org-mobile-${member.userId}`}
                        checked={member.isOrganiser || false}
                        disabled={updatingOrganiserId === member.userId}
                        onCheckedChange={(checked) => onOrganiserChange(member, checked)}
                      />
                      {updatingOrganiserId === member.userId && (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
